import * as THREE from 'three';
import store from '../../redux/store';
import { normalizeLightShadowFields } from './lightShadowUtils.js';

const _worldPosition = new THREE.Vector3();
const _worldQuaternion = new THREE.Quaternion();
const _worldScale = new THREE.Vector3();

export const LIGHT_HELPER_TYPES = new Set([
  'DirectionalLightHelper',
  'PointLightHelper',
  'SpotLightHelper',
  'AmbientLightHelper',
  'AreaLightHelper',
]);

export const LIGHT_ICON_SUFFIX = '-light-icon';

export function getLightHelperVisible(light) {
  if (light?.helperVisible === true || light?.light_helper_visible === true) {
    return true;
  }
  return false;
}

function normalizeLightId(id) {
  if (!id || typeof id !== 'string') return id;
  return id.replace(/-helper$/, '');
}

function getSelectedLightId(override) {
  if (override !== undefined) return override;
  try {
    return store.getState().global_state?.selectedLightId ?? null;
  } catch {
    return null;
  }
}

export function isLightIdSelected(lightOrId, selectedLightId) {
  const selected = normalizeLightId(selectedLightId);
  if (!selected) return false;

  if (typeof lightOrId === 'string') {
    return normalizeLightId(lightOrId) === selected;
  }

  if (!lightOrId) return false;
  const ids = [lightOrId.id, lightOrId._id, lightOrId.name].filter(Boolean);
  return ids.some((id) => normalizeLightId(id) === selected);
}

/** Normalize API / Redux light records after load. */
export function normalizeLightFromApi(light) {
  if (!light) return light;
  return normalizeLightShadowFields({
    ...light,
    id: light._id || light.id,
    helperVisible: getLightHelperVisible(light),
    visible: light.visible !== false,
  });
}

/** Strip editor-only keys; keep API fields including visible + helperVisible. */
export function sanitizeLightPayloadForApi(light) {
  if (!light || typeof light !== 'object') return light;

  const {
    id: _id,
    otherObject: _otherObject,
    createdAt: _createdAt,
    ...apiLight
  } = light;

  const payload = { ...apiLight };

  if (light._id && String(light._id).length > 0) {
    payload._id = light._id;
  }

  payload.helperVisible = getLightHelperVisible(light);
  payload.visible = light.visible !== false;

  Object.keys(payload).forEach((key) => {
    if (payload[key] === undefined) {
      delete payload[key];
    }
  });

  return payload;
}

/** Apply Redux/API visibility to the Three.js light (+ helper in design mode). */
export function applyLightVisibility(
  threeLight,
  light,
  scene,
  { isDesignMode = true, helper: helperOverride = null } = {}
) {
  if (!threeLight || !light) return;

  const visible = light.visible !== false;
  threeLight.visible = visible;

  if (threeLight.isSpotLight && threeLight.target) {
    threeLight.target.visible = visible;
  }

  if (!scene || !isDesignMode) return;

  const lightId = light.id || threeLight.name;
  const helper = helperOverride || resolveLightHelper(scene, lightId);

  if (helper && helper.parent) {
    helper.visible = shouldShowLightHelper(light);
    syncLightHelperFromLight(threeLight, helper);
  }

  const icon = resolveLightIcon(scene, lightId);
  if (icon) {
    icon.visible = shouldShowLightHelper(light);
    if (icon.visible) {
      syncLightIconFromLight(threeLight, icon);
    }
  }
}

export function lightTypeHasHelper(lightType) {
  return (
    lightType === 'Directional Light' ||
    lightType === 'Point Light' ||
    lightType === 'Spot Light' ||
    lightType === 'Area Light' ||
    lightType === 'Ambient Light'
  );
}

export function findLightRecord(lightId, lightData, scene = null) {
  if (!lightId || !Array.isArray(lightData)) return null;

  const direct =
    lightData.find(
      (l) =>
        l.id === lightId ||
        l.name === lightId ||
        l._id === lightId ||
        l.temp_file_name === lightId
    ) || null;

  if (direct) return direct;

  if (!scene) return null;

  const sceneLight = scene.getObjectByName(lightId);
  if (!sceneLight?.isLight) return null;

  return (
    lightData.find(
      (l) =>
        l.id === sceneLight.name ||
        l.name === sceneLight.name ||
        l._id === sceneLight.name
    ) || null
  );
}

/** Build a light record for visibility when Redux lookup fails. */
export function resolveLightVisibilityRecord(lightId, lightData, scene) {
  const record = findLightRecord(lightId, lightData, scene);
  if (record) return record;

  const sceneLight = scene?.getObjectByName(lightId);
  if (!sceneLight) {
    return { id: lightId, visible: true, helperVisible: false };
  }

  return {
    id: lightId,
    visible: sceneLight.visible !== false,
    helperVisible: false,
  };
}

export function resolveLightHelper(scene, lightId) {
  if (!scene || !lightId) return null;
  return scene.getObjectByName(`${lightId}-helper`);
}

export function resolveLightIcon(scene, lightId) {
  if (!scene || !lightId) return null;
  return scene.getObjectByName(`${lightId}${LIGHT_ICON_SUFFIX}`);
}

/** Keep editor icon at the light's world position (icon lives on scene root). */
export function syncLightIconFromLight(threeLight, icon) {
  if (!threeLight || !icon) return;

  threeLight.updateMatrixWorld(true);
  threeLight.getWorldPosition(_worldPosition);
  icon.position.copy(_worldPosition);
}

/** Find helpers anywhere under the scene (not only direct children). */
export function getAllSceneLightHelpers(scene) {
  if (!scene) return [];
  const helpers = [];
  scene.traverse((child) => {
    if (LIGHT_HELPER_TYPES.has(child.type)) {
      helpers.push(child);
    }
  });
  return helpers;
}

export function getSceneLightHelpers(scene) {
  return getAllSceneLightHelpers(scene);
}

/** Find light icons anywhere under the scene. */
export function getAllSceneLightIcons(scene) {
  if (!scene) return [];
  const icons = [];
  scene.traverse((child) => {
    if (child.userData?.isEditorLightIcon) {
      icons.push(child);
    }
  });
  return icons;
}

export function isSceneLight(object) {
  return !!(
    object?.isLight ||
    object?.type === 'DirectionalLight' ||
    object?.type === 'PointLight' ||
    object?.type === 'SpotLight' ||
    object?.type === 'AmbientLight' ||
    object?.type === 'RectAreaLight'
  );
}

export function findSceneFromObject(object) {
  let current = object;
  while (current?.parent) {
    if (current.parent.isScene) return current.parent;
    current = current.parent;
  }
  return null;
}

/** Sync helper position / rotation / scale from its light after animation or transform. */
export function syncLightHelperFromLight(threeLight, helper) {
  if (!threeLight || !helper) return;

  threeLight.updateMatrixWorld(true);

  if (helper.type === 'AreaLightHelper' || helper.type === 'AmbientLightHelper') {
    threeLight.getWorldPosition(_worldPosition);
    threeLight.getWorldQuaternion(_worldQuaternion);
    threeLight.getWorldScale(_worldScale);
    helper.position.copy(_worldPosition);
    helper.quaternion.copy(_worldQuaternion);
    helper.scale.copy(_worldScale);
    return;
  }

  if (typeof helper.update === 'function') {
    helper.update();
  }

  threeLight.getWorldPosition(_worldPosition);
  threeLight.getWorldQuaternion(_worldQuaternion);
  threeLight.getWorldScale(_worldScale);
  helper.position.copy(_worldPosition);
  helper.quaternion.copy(_worldQuaternion);
  helper.scale.copy(_worldScale);
}

export function syncLightHelperForSceneObject(scene, object) {
  if (!scene || !object?.name || !isSceneLight(object)) return;

  const helper = resolveLightHelper(scene, object.name);
  if (helper) {
    syncLightHelperFromLight(object, helper);
  }

  const icon = resolveLightIcon(scene, object.name);
  if (icon) {
    syncLightIconFromLight(object, icon);
  }
}

/**
 * Helper is shown when helperVisible is true and the light is visible,
 * or temporarily while the light is selected (even if helperVisible is false).
 */
export function shouldShowLightHelper(light, { selectedLightId } = {}) {
  if (!light) return false;
  if (light.visible === false) return false;

  const activeSelectedId = getSelectedLightId(selectedLightId);
  if (isLightIdSelected(light, activeSelectedId)) {
    return true;
  }

  if (!getLightHelperVisible(light)) return false;
  return true;
}

export function resolveLightTransformTarget(
  scene,
  lightId,
  lightData,
  { selectedLightId } = {}
) {
  const light = scene?.getObjectByName(lightId);
  const helper = resolveLightHelper(scene, lightId);
  const record = findLightRecord(lightId, lightData, scene);
  const activeSelectedId = getSelectedLightId(selectedLightId);
  const useHelper =
    helper &&
    (isLightIdSelected(record || { id: lightId, name: lightId }, activeSelectedId) ||
      getLightHelperVisible(record));

  if (useHelper) {
    return helper;
  }
  return light || helper;
}

export function syncAllLightHelpersVisibility(
  scene,
  lightData,
  { hideAll = false, selectedLightId } = {}
) {
  if (!scene) return;

  const activeSelectedId = getSelectedLightId(selectedLightId);

  getSceneLightHelpers(scene).forEach((helper) => {
    if (hideAll) {
      helper.visible = false;
      return;
    }

    const lightId = helper.name?.replace(/-helper$/, '');
    const record = resolveLightVisibilityRecord(lightId, lightData, scene);
    helper.visible = shouldShowLightHelper(record, {
      selectedLightId: activeSelectedId,
    });

    const sceneLight = scene.getObjectByName(lightId);
    if (sceneLight?.isLight && helper.visible) {
      syncLightHelperFromLight(sceneLight, helper);
    }
  });

  getAllSceneLightIcons(scene).forEach((icon) => {
    if (hideAll) {
      icon.visible = false;
      return;
    }

    const lightId =
      icon.userData?.lightId ||
      icon.name?.replace(new RegExp(`${LIGHT_ICON_SUFFIX}$`), '');
    const record = resolveLightVisibilityRecord(lightId, lightData, scene);
    icon.visible = shouldShowLightHelper(record, {
      selectedLightId: activeSelectedId,
    });

    const sceneLight = scene.getObjectByName(lightId);
    if (sceneLight?.isLight && icon.visible) {
      syncLightIconFromLight(sceneLight, icon);
    }
  });
}

/** @deprecated Use syncAllLightHelpersVisibility — selection no longer gates visibility. */
export function syncLightHelpersForSelection(
  scene,
  lightData,
  { show = true } = {}
) {
  syncAllLightHelpersVisibility(scene, lightData, { hideAll: !show });
}

/** Remove all lights and editor helpers from the scene (e.g. before publish reload). */
export function clearSceneLightsAndHelpers(scene) {
  if (!scene) return;

  const toRemove = new Set();
  scene.traverse((obj) => {
    if (obj.isLight) {
      toRemove.add(obj);
      if (obj.isSpotLight && obj.target) {
        toRemove.add(obj.target);
      }
    }
    if (LIGHT_HELPER_TYPES.has(obj.type)) {
      toRemove.add(obj);
    }
    if (typeof obj.name === 'string' && obj.name.endsWith(LIGHT_ICON_SUFFIX)) {
      toRemove.add(obj);
    }
    if (obj.userData?.isEditorLightIcon) {
      toRemove.add(obj);
    }
  });

  toRemove.forEach((obj) => {
    if (obj.parent) {
      obj.parent.remove(obj);
    }
  });
}

/** True when Redux knows this id but the Three.js light may not exist yet. */
export function isPendingLightSelectionId(lightId) {
  if (!lightId || typeof lightId !== 'string') return false;
  try {
    const lightData = store.getState().assets_object?.light_data || [];
    return lightData.some(
      (l) =>
        l.id === lightId || l.temp_file_name === lightId || l._id === lightId
    );
  } catch {
    return lightId.startsWith('light-');
  }
}

/** Wait for useLightHook to create the light, then select it (avoids camera fallback). */
export function scheduleSelectLightInScene(scene, lightId, selectObjectInScene) {
  if (!scene || !lightId || typeof selectObjectInScene !== 'function') return;

  const attemptSelect = (remaining = 120) => {
    const lightObj = scene.getObjectByName(lightId);
    if (lightObj?.isLight) {
      selectObjectInScene(lightObj);
      return;
    }
    if (remaining > 0) {
      requestAnimationFrame(() => attemptSelect(remaining - 1));
    }
  };

  attemptSelect();
}
